import { Injectable, inject } from '@angular/core';
import { tap, catchError } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { CommonApiFuncService, StorageService, StorageType } from '@anddone/ui-utilities';
import { APIConstants } from '../constants/api.constant';
import { of } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  loggedInUserData: any;
  public paymentDetailUpdate = new BehaviorSubject(false);
  currentPaymentDetailUpdate = this.paymentDetailUpdate.asObservable();
  private commonAPIFuncService = inject(CommonApiFuncService);
  private storageService = inject(StorageService);

  getLoggedInData() {
    return JSON.parse(this.storageService.get(StorageType.Session, 'userDetails') ?? '{}');
  }

  getAllTransaction(requestObject?: any) {
    this.loggedInUserData = this.getLoggedInData();

    if (this.loggedInUserData) {
      let url = APIConstants.payment.getTransactions.replace('{allyId}', this.loggedInUserData.id);
      url = `${url}${this.commonAPIFuncService.buildQuery(requestObject)}`;
      const options = {
        customBaseUrl: environment.transactionBaseUrl,
      };
      return this.commonAPIFuncService.get(url, options)?.pipe(
        tap(() => this.commonAPIFuncService.log(`getAllTransaction success`)),
        catchError(this.commonAPIFuncService.handleError('getAllTransaction error'))
      );
    }

    return of(null); // ensures return in all paths
  }

  exportReportCSV(reqObj: any) {
    this.loggedInUserData = this.getLoggedInData();
    let url = APIConstants.payment.exportCSVReport.replace('{allyId}', this.loggedInUserData.id);
    url = `${url}${this.commonAPIFuncService.buildQuery(reqObj)}`;
    const options = {
      customBaseUrl: environment.transactionBaseUrl,
    };
    return this.commonAPIFuncService.getExportReportCSV(url, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`exportReportCSV success`)),
      catchError(this.commonAPIFuncService.handleError(`exportReportCSV failed`))
    );
  }

  exportReportPDF(reqObj: any) {
    this.loggedInUserData = this.getLoggedInData();
    let url = APIConstants.payment.exportPDFReport.replace('{allyId}', this.loggedInUserData.id);
    url = `${url}${this.commonAPIFuncService.buildQuery(reqObj)}`;
    const options = {
      customBaseUrl: environment.transactionBaseUrl,
    };
    return this.commonAPIFuncService.post(url, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`exportReportPDF success`)),
      catchError(this.commonAPIFuncService.handleError(`exportReportPDF failed`))
    );
  }

  emailExportReport(reqObj: any) {
    this.loggedInUserData = this.getLoggedInData();
    let url = APIConstants.payment.emailExportReport.replace('{allyId}', this.loggedInUserData?.id);
    url = `${url}${this.commonAPIFuncService.buildQuery(reqObj)}`;
    const options = {
      customBaseUrl: environment.transactionBaseUrl,
    };
    return this.commonAPIFuncService.post(url, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`emailExportReport success`)),
      catchError(this.commonAPIFuncService.handleError(`emailExportReport failed`))
    );
  }

  getTransactionDetails(transId: string, isRefunded: boolean) {
    this.loggedInUserData = this.getLoggedInData();
    const reqObj = {
      splits: true,
      includeRefundTransactions: isRefunded,
    };
    let url = APIConstants.payment.getTransactionDetails
      .replace('{allyId}', this.loggedInUserData.id)
      .replace('{transId}', transId)
      .replace('{isRefunded}', String(isRefunded));
    url = `${url}${this.commonAPIFuncService.buildQuery(reqObj)}`;
    const options = {
      customBaseUrl: environment.transactionBaseUrl,
      customHeaders: {
        'X-Version': '2.2',
      },
    };
    return this.commonAPIFuncService.get(url, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`getTransactionDetails success`)),
      catchError(this.commonAPIFuncService.handleError('getTransactionDetails error'))
    );
  }

  getTransactionTimeLines(allyId: string, transId: any) {
    const url = APIConstants.payment.getTransactionTimeLines
      .replace('{allyId}', allyId)
      .replace('{transId}', transId);
    const options = {
      customBaseUrl: environment.transactionBaseUrl,
    };
    return this.commonAPIFuncService.get(url, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`getTransactionTimeLines success`)),
      catchError(this.commonAPIFuncService.handleError('getTransactionTimeLines Error'))
    );
  }

  checkRefundEligbility(allyId: string, transactionId: string) {
    const url = APIConstants.payment.checkRefundEligbility
      .replace('{allyId}', allyId)
      .replace('{transactionId}', transactionId);
    const options = {
      customBaseUrl: environment.transactionBaseUrl,
    };
    return this.commonAPIFuncService.get(url, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`checkRefundEligbility success`)),
      catchError(this.commonAPIFuncService.handleError(`checkRefundEligbility failed`))
    );
  }

  initiateRefundTransaction(reqObj: any, allyId: string, transactionId: string) {
    const url = APIConstants.payment.initiateRefundTransaction
      .replace('{allyId}', allyId)
      .replace('{transactionId}', transactionId);
    const options = {
      customBaseUrl: environment.transactionBaseUrl,
    };
    return this.commonAPIFuncService.post(url, reqObj, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`initiateRefundTransaction success`)),
      catchError(this.commonAPIFuncService.handleError(`initiateRefundTransaction failed`))
    );
  }
}
